import React from 'react';
import { FiArrowDown, FiDatabase, FiFileText } from 'react-icons/fi';

import { Button, Select } from '@/shared/components/ui';
import type { DatabaseResponse } from '@troia-v3/types';

import type { MigrationBlock } from '../../services/promptMigrationApi';

interface MigrationStepCardProps {
  block: MigrationBlock;
  currentStep: number;
  totalSteps: number;
  isMigrating: boolean;
  onMigrate: () => void;
  onKeep: () => void;
  /**
   * Bases de documentos disponíveis pro agente. Quando há 2+, o card
   * renderiza um select inline pra o admin escolher onde criar este
   * doc específico (útil quando o prompt tem otimizações comerciais
   * + suporte que vão em bases diferentes). Quando há 1, o select é
   * omitido e a base é mostrada como texto.
   */
  documentDatabases: DatabaseResponse[];
  selectedDatabaseId: string;
  onChangeDatabase: (databaseId: string) => void;
}

const ACTION_LABELS: Record<string, { badge: string; badgeBg: string; badgeColor: string; icon: React.ReactNode }> = {
  enrich_existing: {
    badge: 'DOCUMENTO EXISTENTE',
    badgeBg: '#dbeafe',
    badgeColor: '#1e40af',
    icon: <FiFileText size={14} />,
  },
  create_document: {
    badge: 'NOVO DOCUMENTO',
    badgeBg: '#dcfce7',
    badgeColor: '#166534',
    icon: <FiFileText size={14} />,
  },
  create_database: {
    badge: 'NOVO BANCO + DOCUMENTO',
    badgeBg: '#fef3c7',
    badgeColor: '#92400e',
    icon: <FiDatabase size={14} />,
  },
};

export function MigrationStepCard({
  block,
  currentStep,
  totalSteps,
  isMigrating,
  onMigrate,
  onKeep,
  documentDatabases,
  selectedDatabaseId,
  onChangeDatabase,
}: MigrationStepCardProps) {
  const actionConfig = ACTION_LABELS[block.action] || ACTION_LABELS.create_document;
  const selectedDatabaseName =
    documentDatabases.find((db) => db.id === selectedDatabaseId)?.name ??
    block.destination.databaseName ??
    'Base padrão';

  return (
    <div
      style={
        {
          // Card vive dentro do PromptMigrationWizard (modal hardcoded light):
          // override das vars pros shared Button/Select renderizarem claro-fixo
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-primary': '#8b5cf6',
          '--theme-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-button-primary': '#8b5cf6',
          '--theme-button-primary-text': '#ffffff',
          '--theme-button-primary-hover': '#7c3aed',
          '--theme-button-secondary': '#ffffff',
          '--theme-button-secondary-text': '#334155',
          '--theme-button-secondary-hover': 'rgba(0, 0, 0, 0.10)',
          '--theme-button-ghost-hover': '#f1f5f9',
        } as React.CSSProperties
      }
    >
      {/* Progress bar */}
      <div style={{ padding: '12px 20px', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                background: i <= currentStep ? '#8b5cf6' : '#e2e8f0',
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              background: actionConfig.badgeBg,
              color: actionConfig.badgeColor,
              fontSize: '11px',
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: '4px',
            }}
          >
            {actionConfig.badge}
          </span>
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            Bloco {currentStep + 1} de {totalSteps}
          </span>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '20px' }}>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '16px', marginBottom: '4px' }}>
          {block.topic}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
          {block.reason}
        </div>

        {/* Red section: will be removed */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626', textTransform: 'uppercase' as const, marginBottom: '6px' }}>
            Será removido do prompt
          </div>
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '12px',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#991b1b',
              lineHeight: 1.6,
              maxHeight: '120px',
              overflowY: 'auto' as const,
              whiteSpace: 'pre-wrap' as const,
            }}
          >
            {block.promptExcerpt}
          </div>
        </div>

        {/* Arrow */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '8px 0' }}>
          <FiArrowDown size={20} style={{ color: '#8b5cf6' }} />
        </div>

        {/* Green section: destination */}
        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#166534', textTransform: 'uppercase' as const, marginBottom: '6px' }}>
            {block.action === 'enrich_existing' ? 'Será adicionado ao documento existente' :
             block.action === 'create_document' ? 'Novo documento será criado' :
             'Novo banco de dados + documento'}
          </div>
          <div
            style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  background: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#166534',
                }}
              >
                {actionConfig.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, color: '#166534', fontSize: '14px' }}>
                  {block.destination.documentName || block.topic}
                </div>
                <div style={{ fontSize: '12px', color: '#15803d' }}>
                  Banco: {selectedDatabaseName}
                </div>
              </div>
            </div>

            {/* Select inline pra escolher a base SÓ quando o agente tem 2+
                bases de documentos vinculadas. O admin pode alternar entre
                blocos (ex: 2 otimizações comerciais → base "Comercial",
                2 otimizações de suporte → base "Suporte"). Com 1 base só,
                não tem porque mostrar — auto-selecionada no wizard. */}
            {documentDatabases.length > 1 && (
              <div style={{ marginTop: '12px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#166534',
                    textTransform: 'uppercase' as const,
                    marginBottom: '4px',
                  }}
                >
                  Salvar em qual base?
                </label>
                <Select
                  value={selectedDatabaseId}
                  onChange={onChangeDatabase}
                  disabled={isMigrating}
                  options={documentDatabases.map((db) => ({ value: db.id, label: db.name }))}
                  data-testid="migration-step-database-select"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '14px 20px',
          borderTop: '1px solid #e2e8f0',
          background: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          variant="secondary"
          onClick={onKeep}
          disabled={isMigrating}
          data-testid="migration-step-keep"
        >
          Manter no Prompt
        </Button>
        <Button
          variant="primary"
          onClick={onMigrate}
          disabled={isMigrating}
          loading={isMigrating}
          data-testid="migration-step-migrate"
        >
          {isMigrating ? 'Migrando...' : 'Migrar →'}
        </Button>
      </div>
    </div>
  );
}
